// Todos left menu — 조직도 매칭 폴더 트리.
//  • 전체 / 🏢 전사 과업 / 부서별 과업(조직도 자동) / 내 페이지(개인)
//  • 조직 폴더(전사·부서)는 조직도와 자동 연결 — 이름변경/삭제 불가, 선택만 가능
//  • 개인 페이지는 우클릭/호버로 이름 변경·아이콘·세부 페이지·정렬·삭제
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../shared/api/client";
import { I } from "../shared/Icon";
import { toast } from "../shared/stores/toasts";
import { useAuth } from "../shared/stores/auth";
import { ContextMenu, type CtxItem } from "./SubNav";
import { IconPicker } from "../shared/components/IconPicker";

export type TodoPage = {
  id: number;
  title: string;
  icon: string;
  icon_color: string | null;
  parent_id: number | null;
  sort_order: number;
  scope: "PERSONAL" | "COMPANY" | "DEPARTMENT" | "PROJECT";
  department_id: number | null;
  is_system: boolean;
  is_archived: boolean;
};

const DEFAULT_ICON = "📋";

function pageIcon(p: TodoPage) {
  // Legacy/default "FileText" string → emoji fallback.
  return p.icon && p.icon !== "FileText" ? p.icon : "📄";
}

// ── 인박스/보드 카드를 폴더로 드래그 이동 ─────────────────────────────────────
const TODO_DND_MIME = "application/x-todo-id";
async function dropMoveCard(pid: number | null, e: React.DragEvent) {
  const raw = e.dataTransfer.getData(TODO_DND_MIME);
  if (!raw) return;
  try {
    await api.patch(`/todos/${raw}`, { page_id: pid });
    window.dispatchEvent(new Event("todos-inbox-refresh"));
    window.dispatchEvent(new Event("todos-board-refresh"));
    toast.success("이동됨");
  } catch { toast.error("이동 실패"); }
}
function cardDropProps(pid: number | null, setHi?: (b: boolean) => void) {
  return {
    onDragOver: (e: React.DragEvent) => { if (e.dataTransfer.types.includes(TODO_DND_MIME)) { e.preventDefault(); setHi?.(true); } },
    onDragLeave: () => setHi?.(false),
    onDrop: (e: React.DragEvent) => { e.preventDefault(); setHi?.(false); dropMoveCard(pid, e); },
  };
}

// ── Inline rename input ─────────────────────────────────────────────────────

function RenameInput({ initial, onCommit, onCancel }: {
  initial: string; onCommit: (v: string) => void; onCancel: () => void;
}) {
  const [v, setV] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  return (
    <input ref={ref} value={v} onChange={e => setV(e.target.value)}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => { if (e.key === "Enter") { e.stopPropagation(); onCommit(v.trim() || initial); } if (e.key === "Escape") { e.stopPropagation(); onCancel(); } }}
      onBlur={() => onCommit(v.trim() || initial)}
      style={{ flex: 1, fontSize: 12.5, padding: "1px 4px", border: "1px solid var(--p-400,#818cf8)", borderRadius: 3, outline: "none", background: "var(--bg-0)", color: "var(--text-1)", fontFamily: "inherit" }} />
  );
}

// ── 조직(전사·부서) 폴더 행 — 선택만, 편집 불가 ──────────────────────────────

function OrgRow({ page, active, indent, onSelect, onSetIcon, onRename }: {
  page: TodoPage; active: boolean; indent: boolean; onSelect: () => void; onSetIcon?: (ic: string) => void; onRename?: (name: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);
  const [pickingIcon, setPickingIcon] = useState(false);
  const hasMenu = !!(onSetIcon || onRename);
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onContextMenu={hasMenu ? (e => { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); }) : undefined}
      {...cardDropProps(page.id, setHovered)}
      style={{
        display: "flex", alignItems: "center", margin: "1px 6px", borderRadius: 6,
        marginLeft: indent ? 32 : 18,
        background: active ? "rgba(99,102,241,0.12)" : hovered ? "rgba(99,102,241,0.05)" : "transparent",
        transition: "background 0.12s",
      }}>
      <button onClick={onSelect}
        style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: "6px 4px 6px 8px", textAlign: "left", minWidth: 0 }}>
        <span style={{ fontSize: 14, flexShrink: 0 }}>{pageIcon(page)}</span>
        <span style={{ flex: 1, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: active ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: active ? 600 : 400 }}>
          {page.title}
        </span>
      </button>
      {ctx && hasMenu && <ContextMenu x={ctx.x} y={ctx.y} items={[
        ...(onRename ? [{ label: "이름 변경", icon: "✏️", onClick: () => { const n = prompt("폴더 이름", page.title)?.trim(); if (n && n !== page.title) onRename(n); } }] : []),
        ...(onSetIcon ? [{ label: "아이콘 변경", icon: "🎨", onClick: () => setPickingIcon(true) }] : []),
      ]} onClose={() => setCtx(null)} />}
      {pickingIcon && onSetIcon && (
        <div className="t-overlay" onClick={() => setPickingIcon(false)}>
          <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 360, maxWidth: "92vw", padding: 14 }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 14 }}>아이콘 변경 — {page.title}</h3>
            <IconPicker value={pageIcon(page)} onChange={(ic) => { onSetIcon(ic); setPickingIcon(false); }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── 부서 트리 행 (조직도 매칭, 접기/펼치기) ──────────────────────────────────
function DeptRow({ name, icon, depth, hasKids, collapsed, active, onToggle, onSelect, pageId, onSetIcon, onRename }: {
  name: string; icon: string; depth: number; hasKids: boolean; collapsed: boolean;
  active: boolean; onToggle: () => void; onSelect?: () => void; pageId: number | null; onSetIcon?: (ic: string) => void; onRename?: (name: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);
  const [pickingIcon, setPickingIcon] = useState(false);
  const hasMenu = !!(onSetIcon || onRename);
  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onContextMenu={hasMenu ? (e => { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); }) : undefined}
      {...(pageId != null ? cardDropProps(pageId, setHovered) : {})}
      style={{
        display: "flex", alignItems: "center", margin: "1px 6px", borderRadius: 6,
        marginLeft: 18 + depth * 13,
        background: active ? "rgba(99,102,241,0.12)" : hovered ? "rgba(99,102,241,0.05)" : "transparent",
        transition: "background 0.12s",
      }}>
      {hasKids ? (
        <button onClick={onToggle} title={collapsed ? "펼치기" : "접기"}
          style={{ width: 16, height: 20, border: "none", background: "none", cursor: "pointer", color: "var(--text-3)", fontSize: 10, flexShrink: 0, padding: 0 }}>
          {collapsed ? "▸" : "▾"}
        </button>
      ) : <span style={{ width: 16, flexShrink: 0 }} />}
      <button onClick={onSelect} disabled={!onSelect}
        style={{ flex: 1, display: "flex", alignItems: "center", gap: 7, background: "none", border: "none", cursor: onSelect ? "pointer" : "default", padding: "6px 4px 6px 2px", textAlign: "left", minWidth: 0 }}>
        <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
        <span style={{ flex: 1, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: active ? "var(--p-600,#4f46e5)" : onSelect ? "var(--text-2)" : "var(--text-3)", fontWeight: active ? 600 : 400 }}>
          {name}
        </span>
      </button>
      {ctx && hasMenu && <ContextMenu x={ctx.x} y={ctx.y} items={[
        ...(onRename ? [{ label: "이름 변경", icon: "✏️", onClick: () => { const n = prompt("폴더 이름", name)?.trim(); if (n && n !== name) onRename(n); } }] : []),
        ...(onSetIcon ? [{ label: "아이콘 변경", icon: "🎨", onClick: () => setPickingIcon(true) }] : []),
      ]} onClose={() => setCtx(null)} />}
      {pickingIcon && onSetIcon && (
        <div className="t-overlay" onClick={() => setPickingIcon(false)}>
          <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 360, maxWidth: "92vw", padding: 14 }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 14 }}>아이콘 변경 — {name}</h3>
            <IconPicker value={icon} onChange={(ic) => { onSetIcon(ic); setPickingIcon(false); }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── 개인 페이지 행 (편집 가능) ────────────────────────────────────────────────

function PageRow({
  page, active, isChild, isFirst, isLast,
  onSelect, onRename, onDelete, onSetIcon, onAddSub, onMove, onArchive, pageDraggable, onReorder,
}: {
  page: TodoPage; active: boolean; isChild: boolean; isFirst: boolean; isLast: boolean;
  onSelect: () => void; onRename: (v: string) => void; onDelete: () => void;
  onSetIcon: (ic: string) => void; onAddSub: () => void; onMove: (dir: -1 | 1) => void;
  onArchive?: (archived: boolean) => void; pageDraggable?: boolean; onReorder?: (srcId: number, destId: number) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [pickingIcon, setPickingIcon] = useState(false);
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);

  const items: CtxItem[] = [
    ...(!isChild ? [{ label: "세부 페이지 추가", icon: "＋", onClick: onAddSub } as CtxItem] : []),
    { label: "아이콘 변경", icon: "🎨", onClick: () => setPickingIcon(true) },
    { label: "이름 변경", icon: "✏️", onClick: () => setRenaming(true) },
    { sep: true } as CtxItem,
    ...(!isFirst ? [{ label: "위로 이동", icon: "↑", onClick: () => onMove(-1) } as CtxItem] : []),
    ...(!isLast ? [{ label: "아래로 이동", icon: "↓", onClick: () => onMove(1) } as CtxItem] : []),
    ...(onArchive ? [{ sep: true } as CtxItem, { label: page.is_archived ? "보관 해제" : "완료(보관)", icon: "📦", onClick: () => onArchive(!page.is_archived) } as CtxItem] : []),
    { sep: true } as CtxItem,
    { label: "삭제", icon: "🗑️", onClick: onDelete, danger: true },
  ];

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onContextMenu={e => { e.preventDefault(); e.stopPropagation(); setCtx({ x: e.clientX, y: e.clientY }); }}
      onDoubleClick={() => setRenaming(true)}
      draggable={pageDraggable}
      onDragStart={pageDraggable ? (e => { e.dataTransfer.setData("application/x-page-id", String(page.id)); e.dataTransfer.effectAllowed = "move"; }) : undefined}
      onDragOver={e => { const ty = e.dataTransfer.types; if (ty.includes(TODO_DND_MIME) || (onReorder && ty.includes("application/x-page-id"))) { e.preventDefault(); setHovered(true); } }}
      onDragLeave={() => setHovered(false)}
      onDrop={e => {
        setHovered(false);
        const pid = e.dataTransfer.getData("application/x-page-id");
        if (pid) { if (onReorder && Number(pid) !== page.id) { e.preventDefault(); onReorder(Number(pid), page.id); } return; }
        dropMoveCard(page.id, e);
      }}
      style={{
        display: "flex", alignItems: "center", margin: "1px 6px", borderRadius: 6,
        marginLeft: isChild ? 34 : 18,
        background: active ? "rgba(99,102,241,0.12)" : hovered ? "rgba(99,102,241,0.05)" : "transparent",
        transition: "background 0.12s",
      }}>
      <button onClick={onSelect}
        style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", padding: "6px 4px 6px 8px", textAlign: "left", minWidth: 0 }}>
        <span style={{ fontSize: 14, flexShrink: 0, filter: page.icon_color ? `drop-shadow(0 0 3px ${page.icon_color})` : undefined }}>
          {pageIcon(page)}
        </span>
        {renaming ? (
          <RenameInput initial={page.title} onCommit={n => { setRenaming(false); if (n !== page.title) onRename(n); }} onCancel={() => setRenaming(false)} />
        ) : (
          <span onClick={e => { if (active) { e.stopPropagation(); setRenaming(true); } }}
            title={active ? "클릭하여 이름 수정" : undefined}
            style={{ flex: 1, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: active ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: active ? 600 : 400, cursor: active ? "text" : "pointer" }}>
            {page.title}
          </span>
        )}
      </button>

      {hovered && !renaming && (
        <div style={{ display: "flex", gap: 1, paddingRight: 5, flexShrink: 0 }} onMouseDown={e => e.stopPropagation()}>
          {!isChild && (
            <button title="세부 페이지 추가" onClick={e => { e.stopPropagation(); onAddSub(); }} style={rowBtn}><I.Plus size={11} /></button>
          )}
          <button title="이름 변경" onClick={e => { e.stopPropagation(); setRenaming(true); }} style={rowBtn}>✏</button>
          <button title="삭제" onClick={e => { e.stopPropagation(); onDelete(); }} style={{ ...rowBtn, color: "var(--danger,#ef4444)" }}>✕</button>
        </div>
      )}

      {pickingIcon && (
        <div className="t-overlay" onClick={() => setPickingIcon(false)}>
          <div className="t-cmdk" onClick={e => e.stopPropagation()} style={{ width: 360, maxWidth: "92vw", padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 22 }}>{pageIcon(page)}</span>
              <h3 style={{ margin: 0, fontSize: 14, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>아이콘 변경 — {page.title}</h3>
              <button onClick={() => setPickingIcon(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "var(--text-3)" }}>✕</button>
            </div>
            <IconPicker value={pageIcon(page)} onChange={(ic) => { onSetIcon(ic); setPickingIcon(false); }} />
          </div>
        </div>
      )}

      {ctx && <ContextMenu x={ctx.x} y={ctx.y} items={items} onClose={() => setCtx(null)} />}
    </div>
  );
}

const rowBtn: React.CSSProperties = {
  background: "none", border: "none", cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  width: 20, height: 20, borderRadius: 4, fontSize: 11, color: "var(--text-3)", padding: 0,
};

// 접을 수 있는 섹션 헤더 (기본 닫힘 토글)
function ToggleHeader({ open, onToggle, children, action }: { open: boolean; onToggle: () => void; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div onClick={onToggle}
      onDragOver={e => { if (!open && e.dataTransfer.types.includes("application/x-todo-id")) { e.preventDefault(); onToggle(); } }}
      style={{ display: "flex", alignItems: "center", padding: "10px 8px 2px 12px", cursor: "pointer" }}>
      <span style={{ fontSize: 12.5, color: "var(--text-3)", width: 12, flexShrink: 0 }}>{open ? "▾" : "▸"}</span>
      <span style={{ flex: 1, fontSize: 12.5, fontWeight: 700, color: "var(--text-3)", letterSpacing: "0.02em" }}>{children}</span>
      {action && <span onClick={e => e.stopPropagation()} style={{ display: "flex" }}>{action}</span>}
    </div>
  );
}

// ── Main sidebar ────────────────────────────────────────────────────────────

export function TodosSidebar({ onCollapse }: { onCollapse: () => void }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const activePage = params.get("page") || "all";
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).some(r => r === "SUPER_ADMIN" || r === "ADMIN");
  const ROLE_RANK: Record<string, number> = { GUEST: 0, CLIENT: 5, INTERN: 10, EMPLOYEE: 20, TEAM_LEAD: 30, MANAGER: 40, FINANCE_ADMIN: 50, HR_ADMIN: 50, DIVISION_HEAD: 60, C_LEVEL: 80, CEO: 90, SUPER_ADMIN: 100, ADMIN: 100 };
  const myRank = Math.max(0, ...(user?.roles || []).map(r => ROLE_RANK[r] ?? 0));
  const canManageProjects = myRank >= 30;  // 팀장급 이상

  const [pages, setPages] = useState<TodoPage[]>([]);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const newRef = useRef<HTMLInputElement>(null);
  const [addingProject, setAddingProject] = useState(false);
  const [newProjTitle, setNewProjTitle] = useState("");
  const projRef = useRef<HTMLInputElement>(null);
  const [showArchive, setShowArchive] = useState(false);
  // 부서 조직도 트리 (부서별 과업을 계층 폴더트리로)
  type DeptNode = { id: number; name: string; children?: DeptNode[] };
  const [deptTree, setDeptTree] = useState<DeptNode[]>([]);
  const [collapsedDepts, setCollapsedDepts] = useState<Set<number>>(new Set());
  // 섹션 토글 — 기본 닫힘
  const [showCompanySection, setShowCompanySection] = useState(false);
  const [showDeptSection, setShowDeptSection] = useState(false);
  const [showProjectSection, setShowProjectSection] = useState(false);
  const [showPersonalSection, setShowPersonalSection] = useState(false);

  const load = () => api.get<TodoPage[]>("/todos/pages").then(r => setPages(r.data)).catch(() => {});
  const notify = () => window.dispatchEvent(new Event("todos-pages-updated"));

  useEffect(() => {
    load();
    api.get<DeptNode[]>("/iam/departments/tree").then(r => setDeptTree(r.data ?? [])).catch(() => setDeptTree([]));
    const h = () => load();
    window.addEventListener("todos-pages-updated", h);
    return () => window.removeEventListener("todos-pages-updated", h);
    /* eslint-disable-next-line */
  }, []);

  // 스코프별 분류
  const company = pages.filter(p => p.scope === "COMPANY");
  const depts = pages.filter(p => p.scope === "DEPARTMENT").sort((a, b) => a.sort_order - b.sort_order);
  const personal = pages.filter(p => p.scope === "PERSONAL");
  const projects = pages.filter(p => p.scope === "PROJECT" && !p.is_archived).sort((a, b) => a.sort_order - b.sort_order);
  const archivedProjects = pages.filter(p => p.scope === "PROJECT" && p.is_archived).sort((a, b) => a.sort_order - b.sort_order);

  // 트리(루트 + 자식) 빌더 — 개인/프로젝트 공용
  const buildTree = (list: TodoPage[]) => {
    const roots = list.filter(p => p.parent_id == null);
    const out: { page: TodoPage; isChild: boolean }[] = [];
    roots.forEach(root => {
      out.push({ page: root, isChild: false });
      list.filter(p => p.parent_id === root.id).forEach(child => out.push({ page: child, isChild: true }));
    });
    return out;
  };
  const personalOrdered = buildTree(personal);
  const projectOrdered = buildTree(projects);

  const goPage = (id: string) => nav(id === "all" ? "/todos" : `/todos?page=${id}`);

  // 부서 트리(조직도) 렌더 — 부서 과업 페이지를 계층 폴더트리로
  const deptPageByDeptId = new Map(depts.map(p => [p.department_id, p] as const));
  const subtreeHasPage = (n: { id: number; children?: { id: number; name: string; children?: any[] }[] }): boolean =>
    deptPageByDeptId.has(n.id) || (n.children || []).some(subtreeHasPage);
  const toggleDept = (id: number) => setCollapsedDepts(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const renderDeptNodes = (nodes: { id: number; name: string; children?: any[] }[], depth: number): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    nodes.forEach(node => {
      if (!subtreeHasPage(node)) return;
      const page = deptPageByDeptId.get(node.id);
      const kids = (node.children || []).filter(subtreeHasPage);
      const collapsed = collapsedDepts.has(node.id);
      out.push(
        <DeptRow key={node.id} name={page?.title ?? node.name} icon={page ? pageIcon(page) : "🏢"}
          depth={depth} hasKids={kids.length > 0} collapsed={collapsed}
          active={page ? activePage === String(page.id) : false}
          onToggle={() => toggleDept(node.id)}
          onSelect={page ? () => goPage(String(page.id)) : undefined}
          pageId={page?.id ?? null}
          onSetIcon={page ? (ic) => setIcon(page, ic) : undefined}
          onRename={isAdmin && page ? (n => renamePage(page, n)) : undefined} />
      );
      if (kids.length > 0 && !collapsed) out.push(...renderDeptNodes(node.children!, depth + 1));
    });
    return out;
  };
  // 단일 ROOT(전사)는 '전사 과업'과 중복이라 그 하위부터 표시
  const deptTopNodes = (deptTree.length === 1 && !deptPageByDeptId.has(deptTree[0].id))
    ? (deptTree[0].children || []) : deptTree;

  const addPage = async (title: string, parentId?: number, scope: "PERSONAL" | "PROJECT" = "PERSONAL") => {
    const v = title.trim(); if (!v) return;
    try {
      const r = await api.post<TodoPage>("/todos/pages", { title: v, icon: scope === "PROJECT" ? "🚀" : DEFAULT_ICON, parent_id: parentId ?? null, scope });
      await load(); notify();
      goPage(String(r.data.id));
    } catch (e: any) { toast.error("페이지 추가 실패", e?.response?.data?.error?.message ?? ""); }
  };
  const reorderProject = async (srcId: number, destId: number) => {
    const ids = projects.map(p => p.id);
    const from = ids.indexOf(srcId); if (from < 0) return;
    ids.splice(from, 1);
    const to = ids.indexOf(destId);
    ids.splice(to < 0 ? ids.length : to, 0, srcId);
    try { await api.put("/todos/pages/reorder", { ids }); load(); notify(); }
    catch { toast.error("순서 변경 실패"); }
  };
  const archivePage = async (p: TodoPage, archived: boolean) => {
    try { await api.patch(`/todos/pages/${p.id}`, { is_archived: archived }); load(); notify(); toast.success(archived ? "보관됨" : "보관 해제됨"); }
    catch { toast.error(archived ? "보관 실패" : "보관 해제 실패"); }
  };
  const addSub = async (parentId: number, scope: "PERSONAL" | "PROJECT" = "PERSONAL") => {
    const title = prompt("세부 페이지 이름")?.trim();
    if (!title) return;
    await addPage(title, parentId, scope);
  };
  const renamePage = async (p: TodoPage, title: string) => {
    try { await api.patch(`/todos/pages/${p.id}`, { title }); load(); notify(); } catch { toast.error("이름 변경 실패"); }
  };
  const setIcon = async (p: TodoPage, icon: string) => {
    try { await api.patch(`/todos/pages/${p.id}`, { icon }); load(); notify(); } catch { toast.error("아이콘 변경 실패"); }
  };
  const deletePage = async (p: TodoPage) => {
    const hasChildren = personal.some(x => x.parent_id === p.id);
    const msg = hasChildren
      ? `"${p.title}" 페이지와 세부 페이지를 삭제하시겠습니까?\n(할 일은 삭제되지 않고 '전체'에 남습니다)`
      : `"${p.title}" 페이지를 삭제하시겠습니까?\n(할 일은 삭제되지 않고 '전체'에 남습니다)`;
    if (!confirm(msg)) return;
    try {
      await api.delete(`/todos/pages/${p.id}`);
      load(); notify();
      if (activePage === String(p.id)) goPage("all");
    } catch { toast.error("삭제 실패"); }
  };
  const movePage = async (p: TodoPage, dir: -1 | 1) => {
    const siblings = personal.filter(x => x.parent_id === p.parent_id);
    const ids = siblings.map(x => x.id);
    const i = ids.indexOf(p.id), j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      const others = personal.filter(x => x.parent_id !== p.parent_id).map(x => x.id);
      const r = await api.put<TodoPage[]>("/todos/pages/reorder", { ids: [...ids, ...others] });
      // reorder는 개인 페이지만 반환 → 조직 페이지와 합쳐 다시 로드
      void r; load(); notify();
    } catch { toast.error("순서 변경 실패"); load(); }
  };

  const itemBtn = (id: string, icon: string, label: string) => {
    const on = activePage === id;
    return (
      <button onClick={() => goPage(id)}
        style={{ display: "flex", alignItems: "center", gap: 7, width: "100%", border: "none", cursor: "pointer", textAlign: "left", padding: "6px 10px", borderRadius: 6, fontSize: 12.5,
          background: on ? "rgba(99,102,241,0.12)" : "transparent", color: on ? "var(--p-600,#4f46e5)" : "var(--text-2)", fontWeight: on ? 600 : 400 }}>
        <span>{icon}</span><span>{label}</span>
      </button>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "14px 12px 10px", borderBottom: "1px solid var(--border)", flexShrink: 0, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>할 일</h3>
          <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--text-3)" }}>조직 과업 · 보드</p>
        </div>
        <button onClick={onCollapse} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: 2, borderRadius: 4 }} title="접기"><I.PanelL size={14} /></button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "6px 0 10px" }}>
        {/* 대시보드 (랜딩) */}
        <div style={{ padding: "0 8px" }}>{itemBtn("all", "🏠", "대시보드")}</div>
        {/* 보관함 (취소·보관된 카드) */}
        <div style={{ padding: "0 8px" }}>{itemBtn("archive", "🗃️", "보관함")}</div>

        {/* 전사 과업 (토글) */}
        {company.length > 0 && (
          <>
            <ToggleHeader open={showCompanySection} onToggle={() => setShowCompanySection(v => !v)}>🏢 전사 과업</ToggleHeader>
            {showCompanySection && company.map(p => (
              <OrgRow key={p.id} page={p} active={activePage === String(p.id)} indent={false} onSelect={() => goPage(String(p.id))} onSetIcon={ic => setIcon(p, ic)} onRename={isAdmin ? (n => renamePage(p, n)) : undefined} />
            ))}
          </>
        )}

        {/* 부서별 과업 (조직도 매칭, 폴더트리 토글) */}
        {depts.length > 0 && (
          <>
            <ToggleHeader open={showDeptSection} onToggle={() => setShowDeptSection(v => !v)}>🗂️ 부서별 과업</ToggleHeader>
            {showDeptSection && (deptTopNodes.length > 0
              ? renderDeptNodes(deptTopNodes, 0)
              : depts.map(p => (
                  <OrgRow key={p.id} page={p} active={activePage === String(p.id)} indent onSelect={() => goPage(String(p.id))} onSetIcon={ic => setIcon(p, ic)} onRename={isAdmin ? (n => renamePage(p, n)) : undefined} />
                )))}
          </>
        )}

        {/* 프로젝트별 과업 (팀장급↑ 관리, 토글) — 내 페이지 위 */}
        <ToggleHeader open={showProjectSection} onToggle={() => setShowProjectSection(v => !v)} action={
          canManageProjects ? (
            <button title="프로젝트 추가" onClick={() => { setShowProjectSection(true); setAddingProject(true); setTimeout(() => projRef.current?.focus(), 0); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: "2px 6px", borderRadius: 3, display: "flex", alignItems: "center" }}><I.Plus size={12} /></button>
          ) : undefined
        }>🚀 프로젝트별 과업</ToggleHeader>
        {showProjectSection && <>
        {projectOrdered.map(({ page, isChild }) => (
          canManageProjects ? (
            <PageRow key={page.id} page={page} active={activePage === String(page.id)} isChild={isChild}
              isFirst isLast pageDraggable={!isChild} onReorder={reorderProject}
              onSelect={() => goPage(String(page.id))}
              onRename={n => renamePage(page, n)}
              onDelete={() => deletePage(page)}
              onSetIcon={ic => setIcon(page, ic)}
              onAddSub={() => addSub(page.id, "PROJECT")}
              onMove={() => {}}
              onArchive={a => archivePage(page, a)} />
          ) : (
            <OrgRow key={page.id} page={page} active={activePage === String(page.id)} indent={isChild} onSelect={() => goPage(String(page.id))} />
          )
        ))}
        {projects.length === 0 && !addingProject && (
          <div style={{ padding: "8px 16px", fontSize: 12, color: "var(--text-3)", lineHeight: 1.6 }}>{canManageProjects ? "프로젝트가 없습니다. ＋ 로 추가하세요." : "프로젝트가 없습니다."}</div>
        )}
        {canManageProjects && addingProject && (
          <div style={{ display: "flex", gap: 4, margin: "2px 6px", padding: "4px 8px", alignItems: "center", background: "rgba(99,102,241,0.07)", borderRadius: 6, border: "1px dashed var(--p-400,#818cf8)" }}>
            <span style={{ fontSize: 14 }}>🚀</span>
            <input ref={projRef} value={newProjTitle} placeholder="프로젝트 이름…" onChange={e => setNewProjTitle(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { addPage(newProjTitle, undefined, "PROJECT"); setNewProjTitle(""); setAddingProject(false); } if (e.key === "Escape") { setAddingProject(false); setNewProjTitle(""); } }}
              onBlur={() => { if (newProjTitle.trim()) addPage(newProjTitle, undefined, "PROJECT"); setNewProjTitle(""); setAddingProject(false); }}
              style={{ flex: 1, fontSize: 12, border: "none", outline: "none", background: "transparent", color: "var(--text-1)", fontFamily: "inherit" }} />
          </div>
        )}
        </>}

        {/* 내 페이지 (개인, 토글) */}
        <ToggleHeader open={showPersonalSection} onToggle={() => setShowPersonalSection(v => !v)} action={
          <button title="페이지 추가" onClick={() => { setShowPersonalSection(true); setAdding(true); setTimeout(() => newRef.current?.focus(), 0); }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-3)", padding: "2px 6px", borderRadius: 3, display: "flex", alignItems: "center" }}><I.Plus size={12} /></button>
        }>📄 내 페이지</ToggleHeader>
        {showPersonalSection && <>
        {personalOrdered.map(({ page, isChild }) => {
          const siblings = personal.filter(x => x.parent_id === page.parent_id);
          const sIdx = siblings.findIndex(x => x.id === page.id);
          return (
            <PageRow key={page.id} page={page} active={activePage === String(page.id)} isChild={isChild}
              isFirst={sIdx === 0} isLast={sIdx === siblings.length - 1}
              onSelect={() => goPage(String(page.id))}
              onRename={n => renamePage(page, n)}
              onDelete={() => deletePage(page)}
              onSetIcon={ic => setIcon(page, ic)}
              onAddSub={() => addSub(page.id)}
              onMove={dir => movePage(page, dir)} />
          );
        })}

        {personal.length === 0 && !adding && (
          <div style={{ padding: "8px 16px", fontSize: 12, color: "var(--text-3)", lineHeight: 1.6 }}>
            개인 보드가 없습니다. ＋ 로 추가하세요.
          </div>
        )}

        {adding && (
          <div style={{ display: "flex", gap: 4, margin: "2px 6px", padding: "4px 8px", alignItems: "center", background: "rgba(99,102,241,0.07)", borderRadius: 6, border: "1px dashed var(--p-400,#818cf8)" }}>
            <span style={{ fontSize: 14 }}>{DEFAULT_ICON}</span>
            <input ref={newRef} value={newTitle} placeholder="페이지 이름…" onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { addPage(newTitle); setNewTitle(""); setAdding(false); } if (e.key === "Escape") { setAdding(false); setNewTitle(""); } }}
              onBlur={() => { if (newTitle.trim()) addPage(newTitle); setNewTitle(""); setAdding(false); }}
              style={{ flex: 1, fontSize: 12, border: "none", outline: "none", background: "transparent", color: "var(--text-1)", fontFamily: "inherit" }} />
          </div>
        )}
        </>}

        {/* 보관(완료 프로젝트) — 접힘, 과업 드래그 보관용 */}
        {archivedProjects.length > 0 && (
          <>
            <ToggleHeader open={showArchive} onToggle={() => setShowArchive(v => !v)}>📦 보관 (완료 프로젝트) {archivedProjects.length}</ToggleHeader>
            {showArchive && archivedProjects.map(page => (
              <PageRow key={page.id} page={page} active={activePage === String(page.id)} isChild={false}
                isFirst isLast
                onSelect={() => goPage(String(page.id))}
                onRename={n => renamePage(page, n)}
                onDelete={() => deletePage(page)}
                onSetIcon={ic => setIcon(page, ic)}
                onAddSub={() => {}}
                onMove={() => {}}
                onArchive={a => archivePage(page, a)} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
